import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Loader2, Check, X, Trash2, Clock, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminLayout } from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = ['all', 'pending', 'approved', 'rejected'];
const STATUS_LABELS = { all: 'Alle', pending: 'Ausstehend', approved: 'Genehmigt', rejected: 'Abgelehnt' };

function StatusBadge({ status }) {
  const map = {
    pending:  'bg-amber-500/10 text-amber-400 border-amber-500/20',
    approved: 'bg-green-500/10 text-green-400 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  const icons = { pending: Clock, approved: CheckCircle, rejected: XCircle };
  const Icon = icons[status] || Clock;
  const label = { pending: 'Ausstehend', approved: 'Genehmigt', rejected: 'Abgelehnt' }[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${map[status]}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
}

function StarDisplay({ value }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-700 fill-slate-700'}`} />
      ))}
    </span>
  );
}

export function AdminReviews() {
  const { authHeaders } = useAdminAuth();
  const [tab, setTab] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await axios.get(`${API}/admin/reviews`, { headers: authHeaders() });
      const data = all.data;
      setReviews(data);
      setCounts({
        all: data.length,
        pending:  data.filter(r => r.status === 'pending').length,
        approved: data.filter(r => r.status === 'approved').length,
        rejected: data.filter(r => r.status === 'rejected').length,
      });
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await axios.put(`${API}/admin/reviews/${id}/status`, { status }, { headers: authHeaders() });
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm('Bewertung wirklich löschen?')) return;
    await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
    await load();
  };

  const filtered = tab === 'all' ? reviews : reviews.filter(r => r.status === tab);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Bewertungen</h1>
          <p className="text-sm text-slate-500 mt-0.5">{counts.all} gesamt · {counts.pending} ausstehend</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800 mb-6 overflow-x-auto">
          {STATUS_TABS.map(s => (
            <button key={s} onClick={() => setTab(s)}
              data-testid={`review-tab-${s}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                tab === s ? 'text-cyan-400 border-cyan-400' : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}>
              {STATUS_LABELS[s]}
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${tab === s ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-500'}`}>
                {counts[s]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500 text-sm">
            Keine Bewertungen in dieser Kategorie.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(r => (
              <div key={r.id} data-testid={`admin-review-${r.id}`}
                className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    {/* Meta */}
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <span className="text-xs text-slate-400">
                        <span className="text-white font-medium">{r.display_name}</span>
                        {' → '}
                        <span className="text-cyan-400">{r.hosting_name}</span>
                      </span>
                      <StarDisplay value={r.stars} />
                      <StatusBadge status={r.status} />
                      <span className="text-xs text-slate-600">{r.created_at?.slice(0,10)}</span>
                    </div>

                    {/* Category stars */}
                    {(r.stars_price || r.stars_support || r.stars_speed) && (
                      <div className="flex flex-wrap gap-3 mb-2">
                        {r.stars_price   && <span className="text-xs text-slate-500">Preis: {'★'.repeat(r.stars_price)}</span>}
                        {r.stars_support && <span className="text-xs text-slate-500">Support: {'★'.repeat(r.stars_support)}</span>}
                        {r.stars_speed   && <span className="text-xs text-slate-500">Perf.: {'★'.repeat(r.stars_speed)}</span>}
                      </div>
                    )}

                    {r.title && <p className="text-sm font-semibold text-white">{r.title}</p>}
                    <p className="text-sm text-slate-300 mt-1 leading-relaxed">{r.comment}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    {r.status !== 'approved' && (
                      <button onClick={() => setStatus(r.id, 'approved')}
                        data-testid={`approve-btn-${r.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-xs font-medium">
                        <Check className="w-3.5 h-3.5" /> Genehmigen
                      </button>
                    )}
                    {r.status !== 'rejected' && (
                      <button onClick={() => setStatus(r.id, 'rejected')}
                        data-testid={`reject-btn-${r.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors text-xs font-medium">
                        <X className="w-3.5 h-3.5" /> Ablehnen
                      </button>
                    )}
                    <button onClick={() => remove(r.id)}
                      data-testid={`delete-review-btn-${r.id}`}
                      className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
